/* @flow */
/* eslint-env es6,browser */

import { getTopLevelDomain } from '../utils';
import { isSecure } from './browserutil';
import { isBoolean, isString } from './coreutils';

/**
 * @module utils/cookie
 * @description Cookie utility functions
 * @requires utils
 */

/* Cookie handling helper functions */
/**
 * @description Helper to set cookie
 * @param {string} name - cookie key
 * @param {string|number} value - cookie value
 * @param {Document} doc - Document object
 * @param {number} expires - cookie expiry time in sec
 * @param {string} domain - domain for which cookie has to be set
 * @param {string} path - path in which cookie is set
 * @returns {undefined}
 */
export function setCookie(
  name: string = '',
  value: string | number,
  doc?: Document = window.document,
  expires?: number = 0,
  domain?: string = getTopLevelDomain(), // if domain is not passed, use current page's top level domain
  path?: string = '/',
  secure?: boolean = isSecure()
) {
  name = name || '';
  if (!name.length) {
    return;
  }
  const eDate = new Date();
  expires = (expires || 60 * 60 * 24 * 365) * 1000; // Comvert seconds to milliseconds
  eDate.setTime(eDate.getTime() + expires);
  const cData = {
    [name]: encodeURIComponent(String(value)),
    expires: eDate.toUTCString(),
    sameSite: secure ? 'Strict' : '',
    secure,
    path,
    domain
  };

  doc.cookie = Object.entries(cData)
    .map(([k, v]) => {
      // $FlowFixMe
      if (isString(v) && v.length > 0) {
        return [k, v].join('=');
      }
      if (isBoolean(v) && v === true) {
        return k;
      }
      return '';
    })
    .filter(v => v.length > 0) // remove empty values
    .join(';');
}

/**
 * @description Helper to get cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {string} - cookie value
 */
export function getCookie(
  name: string = '',
  doc?: Document = window.document
): string {
  let ret = '';
  if (name.length > 0 && doc.cookie) {
    const m =
      doc.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]*)\\s*(;|$)`) || [];
    if (m.length >= 3) {
      ret = decodeURIComponent(m[2]);
    }
  }
  return ret;
}

/**
 * @description Helper to delete cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {undefined}
 */
export function deleteCookie(
  name: string = '',
  doc?: Document = window.document
) {
  name = name || '';
  const cExists = getCookie(name, doc);
  if (cExists.length) {
    setCookie(name, cExists, doc, -1);
  }
}
